import ControllerBase from './../../../../global/js/ControllerBase';
import {
	FormStepperValidationEvent,
	FormStepperValidationEventClientServerEnum, OrganismFormStepperEvents
} from "../../organisms/o-form-stepper/o-form-stepper.events";
import {FormstepSelectIconEvents} from "./m-formstep-select-icon.events";
import {
	FormstepSelectImageEvents,
	MoleculeSelectImageOptionChangedEvent
} from "../m-formstep-select-image/m-formstep-select-image.events";
import {Field} from "../m-formstep-resume-slice/m-formstep-resume-slice.data";

interface ComponentRefs {
	/** The HTML <select> element */
	selectIconHiddenSelect: 'selectIconHiddenSelect';
	/** The parent of the clickable divs when selecting an icon */
	iconSelectorParent: 'iconSelectorParent';
}
export default class ControllerSelectIcon extends ControllerBase<ComponentRefs> {
	/** The hidden select */
	private select: HTMLSelectElement;
	/** The wrapper div */
	private iconsWrapper: HTMLDivElement;
	/** The min number of selected icons required. Implement as a data attribute, if needed */
	private minIconsRequired = 1;
	/** The form step that we are on */
	private currentStep: number;
	private stepName: string;
	/** Title to be displayed on the summary step */
	private summaryStepTitle: string;

	private static readonly componentRefs: ComponentRefs = {
		selectIconHiddenSelect: 'selectIconHiddenSelect',
		iconSelectorParent: 'iconSelectorParent'
	};

	constructor(element: HTMLElement) {
		super(element, [], Object.values(ControllerSelectIcon.componentRefs));
		this.init();
	}

	private init(): void {
		this.select = this.$references.selectIconHiddenSelect as HTMLSelectElement;
		this.iconsWrapper = this.$references.iconSelectorParent as HTMLDivElement;
		this.currentStep = Number(this.$element.dataset.currentstep);
		this.observeIconSelection();
		this.domNodeListenerEvents();

		this.stepName = String(this.$element.dataset.stepname);
		this.summaryStepTitle = String(this.$element.dataset.summarysteptitle)
	}

	private domNodeListenerEvents(): void {
		// listen for the o-form-stepper issuing external validation events
		// this will be when the user tries to advance to the next step
		window.addEventListener(FormstepSelectIconEvents.REQUEST_VALIDATION, (event: CustomEvent<FormStepperValidationEvent>) => {
			if(this.isCurrentStep(Number(event.detail.currentStep))) {
				// validate the form and call success or error callback
				const formIsValid = this.validateFormRequiredFields();

				if (formIsValid) {
					if (event.detail.onValidationSuccess) event.detail.onValidationSuccess(FormStepperValidationEventClientServerEnum.Frontend);
					this.addUpdateStepResume(); // Update Summary Step
				} else {
					if (event.detail.onValidationError) event.detail.onValidationError();
				}
			}
		});

		window.addEventListener(FormstepSelectImageEvents.SELECT_OPTION_CHANGED, (event: CustomEvent<MoleculeSelectImageOptionChangedEvent>) => {
			const selectedOptions = event.detail.selectedOptions;

			const promiseDone = new Promise((resolve, reject) => {
				this.iconsWrapper.querySelectorAll('.js-selection').forEach((icon: HTMLDivElement, index, array) => {
					const selectedByIds = icon.dataset.showByIds;

					if(selectedOptions.length > 0){
						if(selectedByIds){
							const selectedByIdsArray = selectedByIds.split(",");

							let show = false;
							const removeOptionIndex = parseInt(icon.dataset.index || '-1', 10);

							// First we check if the current icon option is allowed to be shown
							selectedOptions.forEach(function (selectedImageId) {
								if(selectedByIdsArray.includes(selectedImageId)) {
									show = true;
								}
							});

							if(show) {
								icon.classList.remove('uk-hidden');
							} else {
								icon.classList.add('uk-hidden');
								// Remove also active class
								// the css selected class to apply
								const cssSelectedClass = 'selected';
								const isSelected = icon.classList.contains(cssSelectedClass);

								if(isSelected){
									// remove selected class on js-selection div
									icon.classList.toggle(cssSelectedClass);

									// remove icon options from select
									if (removeOptionIndex > -1) {
										const options = this.select.querySelectorAll<'option'>('option');
										options &&
										options.forEach((option, key) => {
											if (removeOptionIndex === key) {
												// programmatically select/unselect the option in the <select>
												option.selected = false;
											}
										});
									}
								}
							}
						}
					} else {
						icon.classList.remove('uk-hidden');
						icon.style.display = 'inline-flex';
					}

					if (index === array.length -1) resolve(0);
				});
			});

			promiseDone.then(() => {
				// reset validation score if the step is not valid anymore
				if(!this.validateFormRequiredFields()) {
					this.dispatchCustomEvent(OrganismFormStepperEvents.RESET_VALIDATION_SCORE_BY_STEP, this.currentStep, false, true);
				}
			});
		});
	}

	/**
	 * Observe clicks on icons in the UI
	 * @returns void
	 */
	private observeIconSelection(): void {
		this.iconsWrapper &&
			this.iconsWrapper.querySelectorAll('.js-selection').forEach((icon: HTMLDivElement) => {
				// listen for clicks on the icons
				icon.addEventListener('click', (event) => this.handleClickEvent(event));
			});
	}

	/**
	 * Add a selected css class and update a hidden multiple <select> when clicking on an icon
	 * @param  {MouseEvent|PointerEvent} event
	 * @returns void
	 */
	private handleClickEvent(event: MouseEvent | PointerEvent): void {
		// the css selected class to apply
		const cssSelectedClass = 'selected';
		// the clicked elements
		const target = event.currentTarget as HTMLDivElement;

		// add/remove selected class on js-selection div
		target.classList.toggle(cssSelectedClass);

		// check if the item is currently selected or not
		const isSelected = target.classList.contains(cssSelectedClass);

		// the target's <select> index
		const optionId = parseInt(target.dataset.index || '-1', 10);

		if (optionId > -1) {
			const options = this.select.querySelectorAll<'option'>('option');
			options &&
				options.forEach((option, key) => {
					if (optionId === key) {
						// programmatically select/unselect the option in the <select>
						option.selected = isSelected;
					}
				});
		}

		this.dispatchCustomEvent(OrganismFormStepperEvents.DISABLE_GO_TO_NEXT_STEP, !this.validateFormRequiredFields(), false, true)
	}

	private validateFormRequiredFields(): boolean {
		return this.select.selectedOptions.length >= this.minIconsRequired;
	}

	private isCurrentStep(currentStepFromEvent: number): boolean {
		return this.currentStep == currentStepFromEvent
	}

	/**
	 * Inform the m-form-stepper about the new resume slice
	 */
	private addUpdateStepResume(): void {
		const options = this.select.querySelectorAll<'option'>('option');
		const fields: Field[] = [];

		options &&
		options.forEach((option, key) => {
			if(option.selected) {
				fields.push(
					{
						label: option.label,
						value: ''
					}
				)
			}
		});

		this.dispatchCustomEvent(OrganismFormStepperEvents.RECEIVE_RESUME_SLICE, {
			step: this.currentStep,
			resumeSlices: [{
				id: this.stepName,
				label: this.summaryStepTitle,
				fields: fields,
				icon: 'pen-circle-filled',
				/** The zero-indexed step, which the slice belongs to */
				originalFormStep: this.currentStep,
				isEditable: true
			}]
		}, false, true);
	}
}
